import { IntlShape } from "react-intl";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import EmailDynamicVariable from "@mapmycustomers/shared/types/email/EmailDynamicVariable";
import EmailEntitiesMissedFields from "@mapmycustomers/shared/types/email/EmailEntitiesMissedFields";
import {
  EntitiesSupportedByEmailFeature,
  EntityTypesSupportedByEmailFeature,
} from "@mapmycustomers/shared/types/entity";
import { isDefined } from "@mapmycustomers/shared/util/assert";

import getEmailDynamicVariableDisplayName from "@app/util/email/getFieldDynamicVariableName";

import getFieldModelByEntityType from "../getByEntityType";
import { isCustomField } from "../impl/assert";

interface GetEmailDynamicVariablesInfoArgs {
  entities?: EntitiesSupportedByEmailFeature[];
  entityType: EntityTypesSupportedByEmailFeature;
  includeOnlyParsedDynamicVariables?: boolean;
  intl: IntlShape;
  parsedDynamicVariables: {
    name: EmailDynamicVariable["name"];
    prefix: EmailDynamicVariable["prefix"];
  }[];
}

const getEmailDynamicVariablesInfo = ({
  entities,
  entityType,
  includeOnlyParsedDynamicVariables,
  intl,
  parsedDynamicVariables,
}: GetEmailDynamicVariablesInfoArgs): [EmailDynamicVariable[], EmailEntitiesMissedFields] => {
  const dynamicVariables: EmailDynamicVariable[] = [];
  const entitiesMissedFields: EmailEntitiesMissedFields = {};

  getFieldModelByEntityType(entityType).fields.forEach((field) => {
    if (!field.hasFeature(FieldFeature.EMAIL_DYNAMIC_VARIABLE)) {
      return;
    }

    const entityHasValue = entities?.find((entity) => isDefined(field.getValueFor(entity)));

    const dynamicVariableName = field.emailDynamicVariableName;
    const dynamicVariablePrefix = isCustomField(field) ? "cf" : "std";

    if (
      parsedDynamicVariables.some(
        ({ name, prefix }) => prefix === dynamicVariablePrefix && name === dynamicVariableName
      )
    ) {
      let allEntitiesHaveValues = true;
      entities?.forEach((entity) => {
        const entityHasValue = !!field.getValueFor(entity);
        allEntitiesHaveValues = allEntitiesHaveValues && entityHasValue;
        if (!entityHasValue) {
          if (!entitiesMissedFields[entity.id]) {
            entitiesMissedFields[entity.id] = [];
          }
          entitiesMissedFields[entity.id].push(field.displayName);
        }
      });
    } else if (includeOnlyParsedDynamicVariables) {
      return;
    }

    const dynamicVariableDisplayName = getEmailDynamicVariableDisplayName(intl, entityType, field);
    const dynamicVariableDisplayValue = entityHasValue
      ? field.getFormattedValueFor(entityHasValue)
      : "";

    dynamicVariables.push({
      allEntitiesHaveValues: entities?.every((entity) => !!field.getValueFor(entity)),
      displayName: dynamicVariableDisplayName,
      displayValue: dynamicVariableDisplayValue,
      name: dynamicVariableName,
      prefix: dynamicVariablePrefix,
    });
  });

  return [dynamicVariables, entitiesMissedFields];
};

export default getEmailDynamicVariablesInfo;
